import React from 'react';
import Icon from '../../../components/AppIcon';


const CurrentSubscriptionCard = ({ subscriptionData }) => {
  const planDetails = {
    starter: {
      name: 'Starter',
      color: 'blue',
      features: ['Single gym location', 'Up to 500 members', 'Basic reporting', 'Email support']
    },
    professional: {
      name: 'Professional',
      color: 'purple',
      features: ['2-5 gym locations', 'Up to 2,000 members', 'Advanced analytics', 'Priority support']
    },
    enterprise: {
      name: 'Enterprise',
      color: 'green',
      features: ['6+ gym locations', 'Unlimited members', 'Custom reporting', 'Dedicated support']
    }
  };

  const plan = planDetails?.[subscriptionData?.currentPlan] || planDetails?.professional;
  const statusConfig = {
    active: { label: 'Active', color: 'green', icon: 'CheckCircle' },
    trial: { label: 'Trial', color: 'blue', icon: 'Clock' },
    pending_approval: { label: 'Pending Approval', color: 'yellow', icon: 'AlertCircle' },
    cancelled: { label: 'Cancelled', color: 'red', icon: 'XCircle' },
    past_due: { label: 'Past Due', color: 'red', icon: 'AlertTriangle' }
  };

  const status = statusConfig?.[subscriptionData?.status] || statusConfig?.active;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-primary to-primary/80 p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold">{plan?.name} Plan</h2>
              <span className={`px-3 py-1 rounded-full text-xs font-medium bg-${status?.color}-500/20 text-white border border-white/30`}>
                <Icon name={status?.icon} className="w-3 h-3 inline mr-1" />
                {status?.label}
              </span>
            </div>
            <p className="text-white/90 text-sm">Your current subscription plan</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">${subscriptionData?.amount}</div>
            <div className="text-white/90 text-sm">per month</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Icon name="Calendar" className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Next Billing Date</p>
              <p className="text-lg font-semibold text-gray-900">
                {new Date(subscriptionData?.nextBillingDate)?.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
              <Icon name="CreditCard" className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Payment Method</p>
              <p className="text-lg font-semibold text-gray-900 capitalize">
                {subscriptionData?.paymentMethod === 'stripe' ? 'Credit Card' : subscriptionData?.paymentMethod}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
              <Icon name="RefreshCw" className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Auto Renewal</p>
              <p className="text-lg font-semibold text-gray-900">
                {subscriptionData?.autoRenew ? 'Enabled' : 'Disabled'}
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Plan Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {plan?.features?.map((feature, index) => (
              <div key={index} className="flex items-center gap-2">
                <Icon name="Check" className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {subscriptionData?.trialEndsAt && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="Info" className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Trial Period Active</p>
                <p className="text-sm text-blue-700 mt-1">
                  Your 7-day trial ends on {new Date(subscriptionData?.trialEndsAt)?.toLocaleDateString()}. 
                  You won't be charged until the trial period ends.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentSubscriptionCard;